import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import CrudTable from "../components/CrudTable";
import PDFButton from "../components/PDFButton";
import Calendar from "../components/Calendar";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const TABS = [
  { key: "calendar", label: "Calendario" },
  { key: "field_walks", label: "Recorridos de Campo" },
  { key: "inspections", label: "Inspecciones de Cumplimiento" },
  { key: "audits", label: "Auditorías Internas" },
  { key: "external", label: "Auditorías Externas" },
  { key: "racs", label: "RACS Globales" },
  { key: "toa", label: "Tarjetas Observación (TOA)" },
];

export default function Audits() {
  const [tab, setTab] = useState("racs");
  return (
    <div data-testid="audits-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Auditorías y RACS</h1>
        <PDFButton endpoint="/pdf/module/racs" filename="reporte_audits.pdf" label="Reporte PDF" />
      </div>
      <div className="tabs-bar">
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`audits-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>
      {tab === "calendar" && <Calendar module="field_walks" title="Recorridos de campo" testId="audits-calendar" />}
      {tab === "field_walks" && <FieldWalks />}
      {tab === "inspections" && <Inspections />}
      {tab === "audits" && <InternalAudits />}
      {tab === "external" && <ExternalAudits />}
      {tab === "racs" && <RACS />}
      {tab === "toa" && <TOA />}
    </div>
  );
}

function FieldWalks() {
  return (
    <CrudTable
      collection="field_walks"
      testIdPrefix="field-walks"
      fields={[
        { key: "date", label: "Fecha", type: "date", required: true },
        { key: "area", label: "Área", required: true },
        { key: "responsible", label: "Responsable" },
        { key: "finding_description", label: "Descripción del hallazgo", type: "textarea", full: true },
        { key: "severity", label: "Severidad", type: "select", options: ["Crítica","Mayor","Menor","Oportunidad"] },
        { key: "immediate_action", label: "Acción inmediata tomada", type: "textarea", full: true },
        { key: "closure_responsible", label: "Responsable de cierre" },
        { key: "due_date", label: "Fecha límite de cierre", type: "date" },
        { key: "observations", label: "Observaciones generales", type: "textarea", full: true },
        { key: "status", label: "Status", type: "select", options: ["Programado", "Ejecutado", "En seguimiento", "Cerrado"] },
      ]}
    />
  );
}

function Inspections() {
  return (
    <CrudTable
      collection="inspections"
      testIdPrefix="inspections"
      fields={[
        { key: "date", label: "Fecha", type: "date" },
        { key: "area", label: "Área" },
        { key: "personnel", label: "Personal evaluado" },
        { key: "score", label: "% Cumplimiento", type: "number" },
        { key: "status", label: "Status", type: "select", options: ["Pendiente", "Ejecutada", "Cerrada"] },
        { key: "notes", label: "Notas", type: "textarea", full: true },
      ]}
    />
  );
}

function InternalAudits() {
  return (
    <CrudTable
      collection="audits"
      filters={{ kind: "internal" }}
      testIdPrefix="internal-audits"
      fields={[
        { key: "code", label: "# Auditoría" },
        { key: "date", label: "Fecha", type: "date" },
        { key: "areas", label: "Áreas" },
        { key: "auditor", label: "Auditor" },
        { key: "companions", label: "Acompañantes" },
        { key: "nc_count", label: "# NCs", type: "number" },
        { key: "status", label: "Status", type: "select", options: ["Programada", "Ejecutada", "Cerrada"] },
        { key: "kind", label: "Tipo", default: "internal", hiddenInForm: true, hidden: true },
      ]}
    />
  );
}

function ExternalAudits() {
  return (
    <CrudTable
      collection="audits"
      filters={{ kind: "external" }}
      testIdPrefix="external-audits"
      fields={[
        { key: "date", label: "Fecha", type: "date" },
        { key: "folio", label: "Folio oficio" },
        { key: "dependency", label: "Dependencia" },
        { key: "type", label: "Tipo" },
        { key: "inspector", label: "Inspector" },
        { key: "companions", label: "Acompañantes" },
        { key: "status", label: "Status", type: "select", options: ["Programada", "Atendida", "Con observaciones", "Cancelada"] },
        { key: "kind", label: "Tipo audit", default: "external", hiddenInForm: true, hidden: true },
      ]}
    />
  );
}

function RACS() {
  return (
    <CrudTable
      collection="racs"
      testIdPrefix="racs"
      fields={[
        { key: "code", label: "# RAC" },
        { key: "description", label: "Descripción", type: "textarea", full: true, required: true },
        { key: "area", label: "Área" },
        { key: "severity", label: "Severidad", type: "select", options: ["Alta", "Media", "Baja"] },
        { key: "origin", label: "Origen", type: "select", options: ["Auditoría interna", "Auditoría externa", "Recorrido", "Inspección", "TOA"] },
        { key: "responsible_close", label: "Responsable cierre" },
        { key: "responsible_verify", label: "Responsable verificación" },
        { key: "due_date", label: "Fecha límite", type: "date" },
        { key: "status", label: "Status", type: "select", options: ["Abierta", "En proceso", "Cerrada"], required: true },
        { key: "recurrent", label: "Reincidente", type: "checkbox" },
      ]}
    />
  );
}

function TOA() {
  return (
    <div data-testid="toa-section">
      <TOACharts />
      <CrudTable
        collection="toa_cards"
        testIdPrefix="toa"
        fields={[
          { key: "code", label: "# TOA (auto / editable)" },
          { key: "date", label: "Fecha", type: "date" },
          { key: "observer_id", label: "# Ficha observador" },
          { key: "observer_name", label: "Nombre observador" },
          { key: "observer_area", label: "Área/Departamento" },
          { key: "type", label: "Tipo de observación", type: "select", options: ["Positiva", "Condición insegura", "Acción correcta", "Acción a corregir"] },
          { key: "category", label: "Categoría de observación", type: "select", options: [
            "Energías Peligrosas",
            "Trabajos en Caliente",
            "Espacios Confinados",
            "Líneas y Tuberías Presurizadas",
            "Equipos de Izaje",
            "Trabajos en Altura",
            "Excavaciones",
            "Tránsito (vehicular y peatonal)",
            "Bloqueo, Tarjeteo y Prueba (BTP/LOTO)",
            "Manejo de Sustancias Químicas / Residuos",
            "Permisos de Trabajo",
            "EPP y Herramientas Manuales",
            "Manejo Defensivo",
            "Orden y Limpieza",
            "Comportamiento / Reglas de Vida",
            "Manejo de Cargas",
            "Otro",
          ] },
          { key: "env_component", label: "Componente ambiental", type: "select", options: ["Residuos", "Agua", "Suelo", "Aire", "Fauna", "Energía", "Ruido", "Otro"] },
          { key: "area", label: "Área de la observación" },
          { key: "observation", label: "Descripción de la observación", type: "textarea", full: true },
          { key: "solution", label: "Descripción de la solución / acción inmediata", type: "textarea", full: true },
          { key: "responsible", label: "Responsable de atención" },
          { key: "due_date", label: "Fecha límite", type: "date" },
          { key: "status", label: "Status", type: "select", options: ["Abierta", "En proceso", "Cerrada"] },
        ]}
      />
    </div>
  );
}

const TOA_TYPES = [
  { key: "Positiva", color: "#1d9e75" },
  { key: "Condición insegura", color: "#f1c40f" },
  { key: "Comportamiento incorrecto", color: "#e74c3c" },
  { key: "Mejora", color: "#3498db" },
];
const MONTHS_LBL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function TOACharts() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [typeFilter, setTypeFilter] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/data/toa_cards").then(({ data }) => setRows(data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!r.date) return false;
    if (new Date(r.date).getFullYear() !== year) return false;
    if (typeFilter && r.type !== typeFilter) return false;
    return true;
  }), [rows, year, typeFilter]);

  // Chart 1 — lines per type per month
  const chart1 = useMemo(() => MONTHS_LBL.map((m, i) => {
    const row = { month: m };
    TOA_TYPES.forEach((t) => {
      row[t.key] = filtered.filter((r) => r.type === t.key && new Date(r.date).getMonth() === i).length;
    });
    return row;
  }), [filtered]);

  // Chart 2 — top 5 areas with most negative obs
  const chart2 = useMemo(() => {
    const counts = {};
    filtered.filter((r) => r.type === "Condición insegura" || r.type === "Comportamiento incorrecto").forEach((r) => {
      const a = r.area || "Sin área"; counts[a] = (counts[a] || 0) + 1;
    });
    return Object.entries(counts).map(([area, count]) => ({ area, count })).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [filtered]);

  // Chart 3 — closure rate by month
  const chart3 = useMemo(() => MONTHS_LBL.map((m, i) => {
    const ms = filtered.filter((r) => new Date(r.date).getMonth() === i);
    const closed = ms.filter((r) => r.status === "Cerrada").length;
    const total = ms.length;
    return { month: m, cerradas: closed, abiertas: total - closed, tasa: total ? (closed / total * 100) : 0 };
  }), [filtered]);

  // KPIs
  const kpi = {
    total: filtered.length,
    positivas: filtered.filter((r) => r.type === "Positiva").length,
    negativas: filtered.filter((r) => r.type === "Condición insegura" || r.type === "Comportamiento incorrecto").length,
    cerradas: filtered.filter((r) => r.status === "Cerrada").length,
  };

  return (
    <div className="card mb-3" data-testid="toa-charts">
      <div className="card-title flex items-center justify-between flex-wrap gap-2">
        <span>Panel TOA — Análisis</span>
        <div className="flex gap-2">
          <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[currentYear, currentYear-1, currentYear-2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Todos los tipos</option>
            {TOA_TYPES.map((t) => <option key={t.key}>{t.key}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-3">
        <ToaKpi testId="toa-kpi-total" label="Total TOAs" value={kpi.total} />
        <ToaKpi testId="toa-kpi-positivas" label="Positivas" value={kpi.positivas} />
        <ToaKpi testId="toa-kpi-negativas" label="Negativas" value={kpi.negativas} />
        <ToaKpi testId="toa-kpi-cierre" label="Tasa cierre" value={kpi.total ? `${(kpi.cerradas / kpi.total * 100).toFixed(1)}%` : "—"} />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div data-testid="toa-chart-tendencia">
          <div className="card-title text-sm">Tendencia mensual por tipo</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chart1}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
              <YAxis stroke="var(--text-dim)" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {TOA_TYPES.map((t) => <Line key={t.key} dataKey={t.key} stroke={t.color} strokeWidth={2} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="toa-chart-top-areas">
          <div className="card-title text-sm">Top 5 áreas con observaciones negativas</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart2} layout="vertical">
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis type="number" stroke="var(--text-dim)" fontSize={11} />
              <YAxis type="category" dataKey="area" stroke="var(--text-dim)" fontSize={11} width={120} />
              <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
              <Bar dataKey="count" fill="#e74c3c" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mb-1" data-testid="toa-chart-cierre">
        <div className="card-title text-sm">Tasa de cierre mensual</div>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={chart3}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
            <YAxis yAxisId="left" stroke="var(--text-dim)" fontSize={11} />
            <YAxis yAxisId="right" orientation="right" stroke="var(--text-dim)" fontSize={11} domain={[0, 100]} />
            <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="cerradas" stackId="a" fill="#1d9e75" />
            <Bar yAxisId="left" dataKey="abiertas" stackId="a" fill="#e74c3c" />
            <Area yAxisId="right" dataKey="tasa" stroke="#3498db" fill="rgba(52,152,219,0.2)" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ToaKpi({ label, value, testId }) {
  return <div className="kpi-card" data-testid={testId}><div className="label">{label}</div><div className="value text-lg">{value}</div></div>;
}
